import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { Lock, Unlock, TrendingUp } from "lucide-react";
import { usePrice, fmtCompact } from "@/hooks/usePrice";

const MILESTONES = [
  { cap: 25000, title: "The Flock Assembles", lore: "First crisis sites scouted. The Screaming Chicken finds its voice." },
  { cap: 50000, title: "Streets Take Notice", lore: "Outreach kits funded. Blankets, meals and hope hit the pavement." },
  { cap: 100000, title: "First Beds Secured", lore: "Emergency accommodation goes live. The movement becomes real." },
  { cap: 250000, title: "Red Tape Trembles", lore: "Case-management program launches — no committees, no delay." },
  { cap: 500000, title: "The Blueprint Goes Live", lore: "Regional expansion. Policy reform advocacy begins in earnest." },
  { cap: 1000000, title: "Unstoppable", lore: "A self-sustaining reform society. A homeless-free future in sight." },
];

export const Milestones = () => {
  const { data, state } = usePrice();
  const mcap = Number(data?.marketCap ?? data?.fdv ?? 0);
  const celebratingRef = useRef(false);

  // Celebrate the moment a new market-cap milestone unlocks (live).
  useEffect(() => {
    if (state !== "ok") return;
    const unlockedCount = MILESTONES.filter((m) => mcap >= m.cap).length;
    const seen = Number(localStorage.getItem("sc_milestone_seen") || 0);
    if (unlockedCount > seen && !celebratingRef.current) {
      celebratingRef.current = true;
      const newest = MILESTONES[unlockedCount - 1];
      const colors = ["#ff007f", "#00f0ff", "#39ff14", "#ffb800"];
      confetti({ particleCount: 90, spread: 70, angle: 60, origin: { x: 0, y: 0.7 }, colors });
      confetti({ particleCount: 90, spread: 70, angle: 120, origin: { x: 1, y: 0.7 }, colors });
      setTimeout(() => confetti({ particleCount: 120, spread: 100, origin: { y: 0.6 }, colors }), 250);
      toast.success(`Milestone unlocked: ${newest.title}!`, {
        description: `${fmtCompact(newest.cap)} market cap reached — new lore revealed.`,
      });
      localStorage.setItem("sc_milestone_seen", String(unlockedCount));
      setTimeout(() => (celebratingRef.current = false), 1200);
    } else if (unlockedCount < seen) {
      localStorage.setItem("sc_milestone_seen", String(unlockedCount));
    }
  }, [state, mcap]);

  const nextIdx = MILESTONES.findIndex((m) => mcap < m.cap);
  const next = nextIdx === -1 ? null : MILESTONES[nextIdx];
  const prevCap = nextIdx <= 0 ? 0 : MILESTONES[nextIdx - 1].cap;
  const progress = next ? Math.min(100, Math.max(0, ((mcap - prevCap) / (next.cap - prevCap)) * 100)) : 100;

  return (
    <section id="milestones" className="relative py-24 sm:py-32 px-5 sm:px-6 border-t border-white/10 overflow-hidden" data-testid="milestones-section">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-2xl mb-14">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#00f0ff] mb-4">Holder Milestones</div>
          <h2 className="font-display font-black uppercase tracking-tighter leading-none text-4xl sm:text-5xl lg:text-6xl">
            Pump unlocks the <span className="hero-gradient-text">lore.</span>
          </h2>
          <p className="font-mono text-sm text-white/50 mt-5 max-w-lg leading-relaxed">
            Every market-cap milestone reveals another piece of the mission — and funds the
            next phase in the real world. Grow the flock, unlock the story.
          </p>
        </div>

        {/* Live progress to next milestone */}
        <div className="rounded-2xl border border-white/10 bg-[#101010] p-6 sm:p-8 mb-8" data-testid="milestone-progress">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">Live Market Cap</div>
              <div className="font-display font-black text-3xl sm:text-4xl text-white tabular-nums">
                {state === "ok" ? fmtCompact(mcap) : state === "loading" ? "···" : "--"}
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">Next Unlock</div>
              <div className="font-mono font-bold text-lg text-[#ffb800]">
                {next ? `${next.title} · ${fmtCompact(next.cap)}` : "ALL UNLOCKED"}
              </div>
            </div>
          </div>
          <div className="h-3 rounded-full bg-black/60 border border-white/10 overflow-hidden">
            <motion.div
              className="h-full rounded-full bg-gradient-to-r from-[#ff007f] via-[#00f0ff] to-[#39ff14]"
              initial={{ width: 0 }}
              whileInView={{ width: `${progress}%` }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            />
          </div>
        </div>

        {/* Milestone grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {MILESTONES.map((m, i) => {
            const unlocked = mcap >= m.cap;
            return (
              <motion.div
                key={m.cap}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ delay: (i % 3) * 0.08, duration: 0.6 }}
                className={`relative rounded-2xl border p-6 transition-colors ${
                  unlocked ? "border-[#39ff14]/40 bg-[#39ff14]/[0.04]" : "border-white/10 bg-[#101010]"
                }`}
                data-testid={`milestone-${i}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="font-mono font-bold text-sm text-white tabular-nums">{fmtCompact(m.cap)}</span>
                  {unlocked ? (
                    <span className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.15em] text-[#39ff14]">
                      <Unlock className="w-3.5 h-3.5" /> Unlocked
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.15em] text-white/35">
                      <Lock className="w-3.5 h-3.5" /> Locked
                    </span>
                  )}
                </div>
                <h3 className="font-display font-bold uppercase tracking-tight text-lg mb-2">
                  {unlocked ? m.title : "??? ???"}
                </h3>
                <p className={`font-mono text-sm leading-relaxed ${unlocked ? "text-white/60" : "text-white/25 blur-[3px] select-none"}`}>
                  {unlocked ? m.lore : "Locked lore — pump the cap to reveal this chapter of the mission."}
                </p>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-8 inline-flex items-center gap-2 font-mono text-xs text-white/40">
          <TrendingUp className="w-4 h-4 text-[#39ff14]" />
          Milestones track live from DexScreener · refreshes automatically
        </div>
      </div>

      <div className="absolute -right-24 top-1/4 w-72 h-72 rounded-full bg-[#00f0ff]/10 blur-3xl pointer-events-none" />
    </section>
  );
};
